import { utcEpochDayKey } from "@/utils/dateEpochKey";

/**
 * Per-session preview model for BulkAddSessionsModal (KB-1460).
 *
 * The bulk-create flow commits one session per entry in the `generatedDates`
 * array, with per-row content carried in `dateListExtras` (keyed by UTC
 * epoch-day, value = a single row object OR an array of row objects when
 * multiple RowFormat rows share the same start date — RowFormat mode does NOT
 * dedupe by date). This module reconstructs that exact creation order so the
 * operator can review the N sessions (content, not just dates) and exclude
 * individual rows before the create runs. Only included rows are passed on to
 * `enqueueBulkSessionJob`.
 *
 * The enumeration mirrors the modal's submit-time reconstruction
 * (BulkAddSessionsModal `handleSubmit` / "Check formatting"): walk the dates in
 * order, and for an array-valued day pull the next element per occurrence.
 */

/** Subset of `dateListExtrasRowSchema` the preview reads. Extra fields are ignored. */
export interface BulkPreviewExtrasRow {
  locationId?: string;
  title?: string;
  offeringId?: string;
  endDate?: Date;
  priceOverride?: number;
  availabilityStatus?: string;
  [key: string]: unknown;
}

export type BulkPreviewExtras =
  | Record<string, BulkPreviewExtrasRow | BulkPreviewExtrasRow[]>
  | undefined;

export interface BulkPreviewEnumeratedRow {
  /** Stable identity for include/exclude — `${epochKey}#${perDayIndex}`. */
  key: string;
  /** Index into the source `dates` array (creation order). */
  dateIndex: number;
  epochKey: string;
  /** 0-based occurrence index within the same UTC day. */
  perDayIndex: number;
  startDate: Date;
  endDate: Date | null;
  extras: BulkPreviewExtrasRow | undefined;
}

/** Stable per-session key. Unique across multiple same-day occurrences. */
export function bulkPreviewRowKey(epochKey: string, perDayIndex: number): string {
  return `${epochKey}#${perDayIndex}`;
}

function coerceEndDate(raw: unknown): Date | null {
  return raw instanceof Date && !Number.isNaN(raw.getTime()) ? raw : null;
}

/**
 * Enumerate the sessions-to-create in creation order. One row per entry in
 * `dates`; `extras` is matched by UTC epoch-day, indexing into array-valued
 * days per occurrence.
 */
export function enumerateBulkPreviewRows(
  dates: Date[],
  extras: BulkPreviewExtras
): BulkPreviewEnumeratedRow[] {
  const perDayRowIndex = new Map<string, number>();
  const out: BulkPreviewEnumeratedRow[] = [];

  for (let idx = 0; idx < dates.length; idx++) {
    const startDate = dates[idx];
    const epochKey = utcEpochDayKey(startDate);
    const perDayIndex = perDayRowIndex.get(epochKey) ?? 0;
    perDayRowIndex.set(epochKey, perDayIndex + 1);

    const raw = extras?.[epochKey];
    let extrasForRow: BulkPreviewExtrasRow | undefined;
    if (Array.isArray(raw)) {
      extrasForRow = raw[perDayIndex] ?? raw[raw.length - 1] ?? undefined;
    } else {
      extrasForRow = raw ?? undefined;
    }

    out.push({
      key: bulkPreviewRowKey(epochKey, perDayIndex),
      dateIndex: idx,
      epochKey,
      perDayIndex,
      startDate,
      endDate: coerceEndDate(extrasForRow?.endDate),
      extras: extrasForRow,
    });
  }

  return out;
}

/**
 * Drop the excluded rows from a bulk submission, returning a filtered
 * `dates` array and a `dateListExtras` record that preserves the original
 * array-vs-single shape per day (so the downstream per-occurrence
 * reconstruction stays aligned with the filtered dates).
 *
 * Pure: never mutates the inputs.
 */
export function applyBulkPreviewExclusions(
  dates: Date[],
  extras: BulkPreviewExtras,
  excludedKeys: ReadonlySet<string>
): { dates: Date[]; extras: BulkPreviewExtras } {
  if (!excludedKeys || excludedKeys.size === 0) {
    return { dates, extras };
  }

  const rows = enumerateBulkPreviewRows(dates, extras);

  const originalWasArray = new Map<string, boolean>();
  if (extras) {
    for (const epochKey of Object.keys(extras)) {
      originalWasArray.set(epochKey, Array.isArray(extras[epochKey]));
    }
  }

  const includedDates: Date[] = [];
  const includedExtrasByDay = new Map<string, BulkPreviewExtrasRow[]>();

  for (const row of rows) {
    if (excludedKeys.has(row.key)) continue;
    includedDates.push(row.startDate);
    if (row.extras !== undefined) {
      const arr = includedExtrasByDay.get(row.epochKey) ?? [];
      arr.push(row.extras);
      includedExtrasByDay.set(row.epochKey, arr);
    }
  }

  if (extras === undefined) {
    return { dates: includedDates, extras: undefined };
  }

  const nextExtras: Record<string, BulkPreviewExtrasRow | BulkPreviewExtrasRow[]> = {};
  for (const [epochKey, arr] of includedExtrasByDay) {
    if (arr.length === 0) continue;
    nextExtras[epochKey] = originalWasArray.get(epochKey) ? arr : arr[0];
  }

  return { dates: includedDates, extras: nextExtras };
}

export interface BulkPreviewRowLabels {
  title: string;
  programLabel: string;
  locationLabel: string;
  priceLabel: string;
  availabilityLabel: string;
}

export interface BulkPreviewLabelContext {
  baseLocationId: string;
  baseLocationName: string;
  topLevel: {
    displayTitle?: string;
    variationKey?: string;
    availabilityStatus?: string;
  };
  /** campLocations doc id → display name (for cross-location org rows). */
  locationNameById?: Record<string, string>;
  /** variation key → displayTitle. */
  programLabelByKey?: Record<string, string>;
  /** variation key → priceRegular (fallback when no per-row price override). */
  programPriceByKey?: Record<string, number | null | undefined>;
  /** availability status id → label. */
  availabilityLabelById?: Record<string, string>;
}

const EM_DASH = "—";

function formatPriceLabel(price: number | null | undefined): string {
  if (price === null || price === undefined || Number.isNaN(price)) return EM_DASH;
  if (price === 0) return "Free";
  // Whole dollars render without cents; otherwise keep two decimals.
  const rounded = Math.round(price * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

/** Resolve the display columns for a single enumerated preview row. */
export function resolveBulkPreviewRowLabels(
  row: BulkPreviewEnumeratedRow,
  ctx: BulkPreviewLabelContext
): BulkPreviewRowLabels {
  const extras = row.extras ?? {};

  const title =
    String(extras.title ?? "").trim() ||
    String(ctx.topLevel.displayTitle ?? "").trim() ||
    EM_DASH;

  const programKey =
    String(extras.offeringId ?? "").trim() ||
    String(ctx.topLevel.variationKey ?? "").trim();
  const programLabel = programKey
    ? ctx.programLabelByKey?.[programKey]?.trim() || programKey
    : EM_DASH;

  const locationId = String(extras.locationId ?? "").trim() || ctx.baseLocationId;
  let locationLabel = EM_DASH;
  if (locationId) {
    locationLabel =
      ctx.locationNameById?.[locationId]?.trim() ||
      (locationId === ctx.baseLocationId ? ctx.baseLocationName.trim() : "") ||
      locationId;
  }

  const priceOverride =
    typeof extras.priceOverride === "number" ? extras.priceOverride : undefined;
  const fallbackPrice = programKey ? ctx.programPriceByKey?.[programKey] : undefined;
  const priceLabel = formatPriceLabel(priceOverride ?? fallbackPrice);

  const availabilityId =
    String(extras.availabilityStatus ?? "").trim() ||
    String(ctx.topLevel.availabilityStatus ?? "").trim();
  const availabilityLabel = availabilityId
    ? ctx.availabilityLabelById?.[availabilityId]?.trim() || availabilityId
    : EM_DASH;

  return { title, programLabel, locationLabel, priceLabel, availabilityLabel };
}
